/**
 * Shared GraphQL WebSocket connection status.
 * Updated by the graphql-ws client lifecycle in apolloClient; consumed by UI hooks.
 */

/** Retries shown as "Reconnecting (n/3)" before the status reads offline. The client keeps retrying after that. */
export const WS_RETRY_ATTEMPTS = 3

/** Wait between the first retries (3 × 3s ≈ 9s repair window). */
export const WS_RETRY_WAIT_MS = 3000

/** Longest wait between retries once the repair window has passed. */
export const WS_RETRY_MAX_WAIT_MS = 15000

/** The client pings this often; a socket that stays silent past the pong timeout is closed and retried. */
export const WS_PING_INTERVAL_MS = 10000
export const WS_PONG_TIMEOUT_MS = 5000
/** Close code the client uses for a socket that stopped answering pings. */
export const WS_PONG_TIMEOUT_CLOSE_CODE = 4408

/**
 * Wait before retry `retries` (0-based, as graphql-ws counts). The first WS_RETRY_ATTEMPTS
 * keep the 3s pace, then the wait doubles up to WS_RETRY_MAX_WAIT_MS. It never gives up:
 * a client that stops retrying leaves every open chat deaf until a reload.
 */
export function wsRetryWaitMs(retries: number): number {
  if (retries < WS_RETRY_ATTEMPTS) {
    return WS_RETRY_WAIT_MS
  }
  return Math.min(WS_RETRY_WAIT_MS * 2 ** (retries - WS_RETRY_ATTEMPTS + 1), WS_RETRY_MAX_WAIT_MS)
}

/** Status for the `attempt`-th retry (1-based): reconnecting inside the repair window, offline after it. */
export function wsRetryState(attempt: number): WsConnectionState {
  return {
    status: attempt > WS_RETRY_ATTEMPTS ? 'offline' : 'reconnecting',
    attempt: Math.min(attempt, WS_RETRY_ATTEMPTS),
    maxAttempts: WS_RETRY_ATTEMPTS,
  }
}

export type WsConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline'

export type WsConnectionState = {
  status: WsConnectionStatus
  /** 1-based attempt while reconnecting; 0 otherwise */
  attempt: number
  maxAttempts: number
}

export const initialWsConnectionState: WsConnectionState = {
  status: 'connecting',
  attempt: 0,
  maxAttempts: WS_RETRY_ATTEMPTS,
}

type Listener = (state: WsConnectionState) => void

let state: WsConnectionState = { ...initialWsConnectionState }
const listeners = new Set<Listener>()

export function getWsConnectionState(): WsConnectionState {
  return state
}

export function setWsConnectionState(next: WsConnectionState): void {
  state = next
  listeners.forEach(listener => listener(state))
}

export function subscribeWsConnectionState(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const reconnectListeners = new Set<() => void>()

/**
 * Called when a retry connects. graphql-ws resubscribes, but events published while the
 * socket was down are gone, so live views re-read what they show.
 */
export function notifyWsReconnected(): void {
  reconnectListeners.forEach(listener => listener())
}

export function subscribeWsReconnected(listener: () => void): () => void {
  reconnectListeners.add(listener)
  return () => {
    reconnectListeners.delete(listener)
  }
}

/** Close codes that should not be retried (auth / policy failures). */
export const WS_DO_NOT_RETRY_CODES = new Set([1008, 1011, 4401, 4403])
